import { useCallback, useEffect } from "react";
import {
  PartyData,
  PartyImageRequest,
  PartyImageResponseEntry,
  ProfileImageResponseEntry,
  useGetProfileImagesMutation,
} from "../../../services/ActionReports/ProfileImageAPI";
import { ProfileTransformResponse } from "../../../services/Profile/profileAPI.types";
import { transformSponsor } from "../KeyInformation/Transforms/transformKeyInformation";
import { PdfFetchProfileImages } from "../shared/types";

interface GetProfileImage {
  affAbo: string;
  imageRefUrl: string;
}

function detectBase64ImageType(base64: string) {
  if (base64.startsWith("iVBORw0KGgo")) return "png";
  if (base64.startsWith("/9j/")) return "jpeg";
  if (base64.startsWith("R0lGODlh") || base64.startsWith("R0lGODdh")) return "gif";
  if (base64.startsWith("UklGR")) return "webp";

  return "unknown";
}
const BASE64 = (type: string) => `data:image/${type};base64,`;
const mapProfileImages = (profileImages?: ProfileImageResponseEntry[]) => {
  return (
    profileImages?.map((e) => {
      const [aff = "", abo = ""] = e.affAbo?.split("-") ?? [];
      const profileRef = e.profileImage.trim();
      const profileImageWithBase = `${BASE64(detectBase64ImageType(profileRef))}${profileRef}`;
      const profileImage = profileRef ? profileImageWithBase : "";
      return { aff, abo, profileImage };
    }) ?? []
  );
};

const mapPartyImages = (partyImages?: PartyImageResponseEntry[]) => {
  return (
    partyImages?.map((e) => {
      const [aff = "", abo = ""] = e.affAbo?.split("-") ?? [];
      const partyId = e.partyId;
      const profileRef = e.profileImage.trim();
      const profileImageWithBase = `${BASE64(detectBase64ImageType(profileRef))}${profileRef}`;
      const profileImage = profileRef ? profileImageWithBase : "";
      return { aff, abo, partyId, profileImage };
    }) ?? []
  );
};

const useGetProfileImage = (entries?: GetProfileImage[], skip = false, partyEntries?: PartyImageRequest) => {
  const [fetchProfileImages, { isLoading, isUninitialized, data: profileImageData }] = useGetProfileImagesMutation();

  //On Trigger Load for profile images
  const pdfFetchProfileImages = useCallback(
    async (profileInfo?: ProfileTransformResponse): Promise<PdfFetchProfileImages | undefined> => {
      const currentPartyEntries: PartyData[] =
        profileInfo?.partyInfo?.map(({ partyId, imageRefUrl }) => {
          return { partyId, imageRefUrl: imageRefUrl ? imageRefUrl : "" };
        }) ?? [];

      const targetAffAbo = `${String(profileInfo?.aboInfo?.affNo).padStart(3, "0")}-${profileInfo?.aboInfo?.aboNo}`;
      const sponsorEntries = transformSponsor(profileInfo?.aboInfo).map((s) => {
        return {
          affAbo: `${s.affNo?.padStart(3, "0")}-${s.aboNo}`,
          imageRefUrl: s.imageRefUrl ?? "",
        };
      });
      const currentEntries: GetProfileImage[] = [
        { affAbo: targetAffAbo, imageRefUrl: profileInfo?.aboInfo?.imageRefUrl ?? "" },
        ...sponsorEntries,
      ];
      if (
        currentEntries.length + currentPartyEntries.length > 0 &&
        (currentEntries.some((e) => e.imageRefUrl !== "") || currentPartyEntries.some((e) => e.imageRefUrl !== ""))
      ) {
        const imageResults = await fetchProfileImages({
          abos: currentEntries,
          parties: currentPartyEntries?.length ? { affAbo: targetAffAbo, partyData: currentPartyEntries } : undefined,
        }).unwrap();

        const profileImages = mapProfileImages(imageResults.images);
        const partyImages = mapPartyImages(imageResults.partyImages);

        return { profileImages, partyImages };
      } else {
        return undefined;
      }
    },
    [fetchProfileImages],
  );

  //Base load utilizing useeffect for profile images
  const entriesKey = entries?.map((e) => `${e.affAbo}:${e.imageRefUrl}`).join(",") ?? "";
  const partyEntriesKey = partyEntries?.partyData.map((e) => `${e.partyId}:${e.imageRefUrl}`).join(",") ?? "";

  const hasImageRefUrls =
    entries?.some((e) => e.imageRefUrl !== "") || (partyEntries?.partyData.some((e) => e.imageRefUrl !== "") ?? false);

  const profileImages = mapProfileImages(profileImageData?.images);

  const partyImages = mapPartyImages(profileImageData?.partyImages);

  useEffect(() => {
    const currentPartyEntries = partyEntries?.partyData ?? [];
    const currentEntries = entries ?? [];
    if (
      currentEntries.length + currentPartyEntries.length > 0 &&
      !skip &&
      (currentEntries.some((e) => e.imageRefUrl !== "") || currentPartyEntries.some((e) => e.imageRefUrl !== ""))
    ) {
      void fetchProfileImages({
        abos: currentEntries,
        parties: partyEntries?.partyData.length ? partyEntries : undefined,
      }).unwrap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entriesKey, skip, fetchProfileImages, partyEntriesKey]);

  const isValid = (entries?.length ?? 0) + (partyEntries?.partyData?.length ?? 0) >= 1;
  const isPending = hasImageRefUrls && isUninitialized;

  return {
    isLoading: isValid ? isLoading || isPending : true,
    profileImage: isValid ? (profileImages?.[0]?.profileImage ?? "") : "",
    profileImages: isValid ? profileImages : [],
    partyImages: isValid ? partyImages : [],
    pdfFetchProfileImages,
  };
};

export default useGetProfileImage;
